import { Temporal } from "@js-temporal/polyfill";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createChoreCompletion,
  type Chore,
  type CreateChoreCompletionInput,
} from "@repo/api/client";
import { z } from "zod";
import { FieldErrors } from "@/components/form/field-errors";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const completionFormSchema = z.object({
  actualDurationMins: z
    .number("Duration must be at least 1 minute")
    .int()
    .positive("Duration must be at least 1 minute")
    .max(24 * 60, "Duration must not be more than 24 hours"),
  completedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, "Invalid date/time"),
});

type CompleteChoreDialogProps = {
  chore: Chore;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function nowLocalDateTimeString(): string {
  const now = Temporal.Now.plainDateTimeISO();
  return `${now.year.toString().padStart(4, "0")}-${now.month.toString().padStart(2, "0")}-${now.day.toString().padStart(2, "0")}T${now.hour.toString().padStart(2, "0")}:${now.minute.toString().padStart(2, "0")}`;
}

export function CompleteChoreDialog({ chore, open, onOpenChange }: CompleteChoreDialogProps) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: CreateChoreCompletionInput) =>
      createChoreCompletion(chore.choreScheduleId, chore.id, data),
  });

  const form = useForm({
    defaultValues: {
      actualDurationMins: chore.expectedDurationMins ?? 30,
      completedAt: nowLocalDateTimeString(),
    },
    validators: {
      onMount: completionFormSchema,
      onBlur: completionFormSchema,
    },
    onSubmit: async ({ value }) => {
      await mutation.mutateAsync({
        actualDurationMins: value.actualDurationMins,
        completedAt: value.completedAt,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      await queryClient.invalidateQueries({
        queryKey: ["chore-schedules", chore.choreScheduleId, "chores", chore.id, "completions"],
      });
      onOpenChange(false);
      form.reset();
    },
  });

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      form.reset();
      mutation.reset();
    }
    onOpenChange(nextOpen);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Complete {chore.name}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="space-y-4"
        >
          <form.Field name="actualDurationMins">
            {(field) => (
              <div className="space-y-1">
                <Label htmlFor="completion-duration">Duration (minutes)</Label>
                <Input
                  id="completion-duration"
                  type="number"
                  min={1}
                  placeholder="e.g. 30"
                  value={field.state.value || ""}
                  onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                  onBlur={field.handleBlur}
                  autoFocus
                />
                <FieldErrors meta={field.state.meta} />
              </div>
            )}
          </form.Field>

          <form.Field name="completedAt">
            {(field) => (
              <div className="space-y-1">
                <Label htmlFor="completion-completed-at">Completed at</Label>
                <Input
                  id="completion-completed-at"
                  type="datetime-local"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                <FieldErrors meta={field.state.meta} />
              </div>
            )}
          </form.Field>

          {mutation.isError && (
            <p className="text-sm text-destructive">
              Failed to record completion. Please try again.
            </p>
          )}

          <DialogFooter>
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting, state.isDirty]}
            >
              {([canSubmit, isSubmitting]) => (
                <Button type="submit" disabled={!canSubmit || isSubmitting}>
                  {isSubmitting ? "Saving…" : "Save"}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
